import type { ContentResult, EventData } from "@/services/sanity/types";

function parseYmd(dateStr: string): { y: number; m: number; d: number } | null {
    const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;
    return {
        y: parseInt(match[1], 10),
        m: parseInt(match[2], 10),
        d: parseInt(match[3], 10),
    };
}

function formatYmdGCal(y: number, m: number, d: number): string {
    const yyyy = String(y).padStart(4, "0");
    const mm = String(m).padStart(2, "0");
    const dd = String(d).padStart(2, "0");
    return `${yyyy}${mm}${dd}`;
}

/**
 * Builds a 1-tap "Add to Google Calendar" link.
 */
export function buildGoogleCalendarUrl(event: EventData): string {
    const title = encodeURIComponent(event.title);
    const details = encodeURIComponent(
        (event.description || "") +
            (event.image ? `\n\nFlyer: ${event.image}` : "") +
            `\n\nEvent via Auckland Church (https://aucklandchurch.com)`
    );
    const location = encodeURIComponent(event.location || event.address || "Auckland, New Zealand");

    const formatGCalDate = (dateStr: string): string => {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return "";
        return d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
    };

    let datesParam = "";
    if (event.isAllDay) {
        const startParts = parseYmd(event.date);
        const endParts = parseYmd(event.endDate || event.date);

        if (startParts && endParts) {
            const startGcal = formatYmdGCal(startParts.y, startParts.m, startParts.d);
            // Google Calendar all-day end date is exclusive (+1 day from inclusive end date)
            const nextDayUtc = new Date(Date.UTC(endParts.y, endParts.m - 1, endParts.d + 1));
            const endGcal = formatYmdGCal(
                nextDayUtc.getUTCFullYear(),
                nextDayUtc.getUTCMonth() + 1,
                nextDayUtc.getUTCDate()
            );
            datesParam = `&dates=${startGcal}/${endGcal}`;
        }
    } else {
        const startUtc = formatGCalDate(event.date);
        let endUtc = event.endDate ? formatGCalDate(event.endDate) : "";

        if (!endUtc && startUtc) {
            // Default to 2 hours after start
            const d = new Date(event.date);
            d.setHours(d.getHours() + 2);
            endUtc = formatGCalDate(d.toISOString());
        }

        if (startUtc && endUtc) {
            datesParam = `&dates=${startUtc}/${endUtc}`;
        }
    }

    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}${datesParam}&details=${details}&location=${location}`;
}

/**
 * Fetches events from the serverless Google Calendar endpoint (/api/events).
 */
export async function fetchCalendarEvents(limit = 10, includePast = false): Promise<ContentResult<EventData[]>> {
    const bustParam = import.meta.env.DEV ? `&bust=${Date.now()}` : "";
    const endpoint = `/api/events?limit=${limit}${includePast ? "&includePast=true" : ""}${bustParam}`;

    try {
        const res = await fetch(endpoint, {
            headers: {
                Accept: "application/json",
            },
        });

        if (!res.ok) {
            throw new Error(`Calendar API returned HTTP ${res.status}`);
        }

        const data = (await res.json()) as {
            events?: EventData[];
            error?: string;
            source?: string;
            calendarUrl?: string;
        };

        if (Array.isArray(data.events) && data.events.length > 0) {
            return {
                data: data.events,
                source: "calendar",
                calendarUrl: data.calendarUrl,
            };
        }

        if (data.error) {
            throw new Error(data.error);
        }

        return {
            data: [],
            source: "calendar",
            calendarUrl: data.calendarUrl,
        };
    } catch (err) {
        return {
            data: [],
            source: "fallback",
            error: err instanceof Error ? err.message : "Unable to reach calendar endpoint",
        };
    }
}
